import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Chip,
  Alert,
  Paper,
  Stack,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import TableChartIcon from "@mui/icons-material/TableChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useNavigation } from "../lib/NavigationContext";
import {
  getRateTable,
  saveRateTable,
  loadRateTable,
  RATE_DIVISOR,
} from "../components/mobility/data/rateTable";
import toast from "../lib/toast";
import { useEffect } from "react";

const ZONES = ["1", "2", "3", "4", "3bis"];
const PACKAGES = ["Base", "Medium", "Collisione", "Kasko"];
const DURATIONS = [12, 24, 36, 48, 60];

export default function AdminRates() {
  const { navigateTo } = useNavigation();
  const [mainTab, setMainTab] = useState(0);

  const [zoneTab, setZoneTab] = useState(0);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [editableRates, setEditableRates] = useState(getRateTable());
  const [companyName, setCompanyName] = useState("Generali Assicurazioni");
  const [productName, setProductName] = useState("Mobility Safe SAT - CVT");

  useEffect(() => {
    setEditableRates(loadRateTable());
  }, []);

  const handleRateChange = (zone, pkg, duration, value) => {
    const newRates = { ...editableRates };
    if (!newRates[zone]) newRates[zone] = {};
    if (!newRates[zone][pkg]) newRates[zone][pkg] = {};
    newRates[zone][pkg][duration] = parseFloat(value) || 0;
    setEditableRates(newRates);
  };

  const saveRates = () => {
    saveRateTable(editableRates);
    toast.success(
      "Tassi salvati: Le modifiche sono state salvate con successo",
    );
  };

  const validateRateTable = () => {
    setIsValidating(true);
    const missing = [],
      warnings = [];
    ZONES.forEach((zone) =>
      PACKAGES.forEach((pkg) =>
        DURATIONS.forEach((duration) => {
          const rate = editableRates[zone]?.[pkg]?.[duration];
          if (rate === undefined || rate === null)
            missing.push({ zone, package: pkg, duration });
          else if (rate === 0) warnings.push({ zone, package: pkg, duration });
        }),
      ),
    );
    setTimeout(() => {
      setValidationResult({ missing, warnings, valid: missing.length === 0 });
      setIsValidating(false);
      if (missing.length === 0)
        toast.success(
          `Validazione completata: Tabella valida. ${warnings.length} avvisi.`,
        );
      else
        toast.error(
          `Validazione fallita: ${missing.length} combinazioni mancanti`,
        );
    }, 500);
  };

  const handleExport = () => {
    let csv = "ZONE,Pacchetto,12,24,36,48,60\n";
    ZONES.forEach((zone) =>
      PACKAGES.forEach((pkg) => {
        const rates = DURATIONS.map(
          (d) => editableRates[zone]?.[pkg]?.[d] || "",
        ).join(",");
        csv += `${zone},${pkg},${rates}\n`;
      }),
    );
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mobility_safe_rates.csv";
    a.click();
    toast.success("Export completato: File CSV scaricato");
  };

  const currentZone = ZONES[zoneTab];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Gestione Accordo Compagnia
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configura tassi, condizioni e prodotto
          </Typography>
        </Box>
      </Box>

      {/* Company & Product */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Nome Compagnia"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Nome Prodotto"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
        </CardContent>
      </Card>

      {/* Action Bar */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
            <Button
              variant="contained"
              color="success"
              startIcon={<SaveIcon />}
              onClick={saveRates}
            >
              Salva Modifiche
            </Button>
            <Button
              variant="outlined"
              startIcon={
                isValidating ? (
                  <RefreshIcon
                    sx={{
                      animation: "spin 1s linear infinite",
                      "@keyframes spin": {
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                ) : (
                  <CheckCircleIcon />
                )
              }
              onClick={validateRateTable}
              disabled={isValidating}
            >
              Valida
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => toast("Importazione: Funzionalità in sviluppo")}
            >
              Importa Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              Esporta CSV
            </Button>
            <Box
              sx={{
                ml: "auto",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <TableChartIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Rate Divisor:
              </Typography>
              <Chip label={RATE_DIVISOR} size="small" variant="outlined" />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Validation Result */}
      {validationResult && (
        <Alert
          severity={validationResult.valid ? "success" : "error"}
          icon={
            validationResult.valid ? <CheckCircleIcon /> : <ErrorOutlineIcon />
          }
          sx={{ mb: 3 }}
        >
          <Typography variant="body2" fontWeight={700}>
            {validationResult.valid ? "Tabella Valida" : "Errori Rilevati"}
          </Typography>
          {validationResult.valid ? (
            `Tutte le ${
              ZONES.length * PACKAGES.length * DURATIONS.length
            } combinazioni presenti. ${
              validationResult.warnings.length
            } avvisi.`
          ) : (
            <>
              {validationResult.missing.length} combinazioni mancanti:
              <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                {validationResult.missing.slice(0, 5).map((m, i) => (
                  <li key={i}>
                    Zona {m.zone} - {m.package} - {m.duration} mesi
                  </li>
                ))}
                {validationResult.missing.length > 5 && (
                  <li>... e altre {validationResult.missing.length - 5}</li>
                )}
              </ul>
            </>
          )}
        </Alert>
      )}

      {/* Main Tabs */}
      <Card elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
            <Tab label="Tabella Tassi" />
            <Tab label="Scoperti e Franchigie" />
          </Tabs>
        </Box>

        {/* Rates Tab */}
        {mainTab === 0 && (
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <TableChartIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Tabella Tassi per Zona (Editabile)
              </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs value={zoneTab} onChange={(_, v) => setZoneTab(v)}>
                {ZONES.map((zone) => (
                  <Tab key={zone} label={`Zona ${zone}`} />
                ))}
              </Tabs>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Pacchetto</strong>
                    </TableCell>
                    {DURATIONS.map((d) => (
                      <TableCell key={d} align="center">
                        <strong>{d} mesi</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PACKAGES.map((pkg) => (
                    <TableRow key={pkg} hover>
                      <TableCell>
                        <Chip
                          label={pkg}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      {DURATIONS.map((duration) => (
                        <TableCell key={duration} align="center" sx={{ p: 1 }}>
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{
                              step: 0.01,
                              style: {
                                textAlign: "center",
                                fontFamily: "monospace",
                                width: 80,
                              },
                            }}
                            value={
                              editableRates[currentZone]?.[pkg]?.[duration] ||
                              ""
                            }
                            onChange={(e) =>
                              handleRateChange(
                                currentZone,
                                pkg,
                                duration,
                                e.target.value,
                              )
                            }
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            ‰
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              * I tassi sono espressi in per mille (‰). Premio = Valore Veicolo
              × (Tasso / {RATE_DIVISOR})
            </Typography>
          </CardContent>
        )}

        {/* Conditions Tab */}
        {mainTab === 1 && (
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <SecurityIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Scoperti, Franchigie e Limiti per Zona
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configura scoperti, franchigie e limiti per ogni garanzia e zona
              geografica
            </Typography>
            <Button
              onClick={() => navigateTo("AdminConditions")}
              variant="contained"
            >
              Gestisci Condizioni
            </Button>
          </CardContent>
        )}
      </Card>
    </Container>
  );
}
